import moment, { Moment } from 'moment';

export class DateHelper {

  /**
   * Convert a Paris civil date and time to a UTC timestamp
   * @param date Selected day
   * @param time Selected time
   * @returns UTC timestamp in milliseconds
   */
  static parisTimestamp(date: Moment, time: Moment): number {
    const utcGuess = Date.parse(`${date.format('YYYY-MM-DD')}T${time.format('HH:mm')}:00Z`);
    const parisClock = new Date(utcGuess)
      .toLocaleString('sv-SE', { timeZone: 'Europe/Paris' })
      .replace(' ', 'T');
    const parisAsUtc = Date.parse(`${parisClock}Z`);
    return utcGuess - (parisAsUtc - utcGuess);
  }

  /**
   * Compute French public holidays for a given year.
   * Mobile holidays (Easter Monday, Ascension, Pentecost Monday) are derived
   * from Easter Sunday via the Meeus/Jones/Butcher algorithm.
   * @param year The year to compute holidays for
   * @returns Array of ISO date strings (YYYY-MM-DD)
   */
  static getFrenchHolidaysForYear(year: number): string[] {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const easterMonth = Math.floor((h + l - 7 * m + 114) / 31);
    const easterDay = ((h + l - 7 * m + 114) % 31) + 1;

    const pad = (n: number): string => String(n).padStart(2, '0');
    const fixed = (month: number, day: number): string => `${year}-${pad(month)}-${pad(day)}`;
    const fromEaster = (n: number): string => {
      const dt = new Date(Date.UTC(year, easterMonth - 1, easterDay + n));
      return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
    };

    return [
      fixed(1, 1),     // New Year's Day
      fromEaster(1),   // Easter Monday
      fixed(5, 1),     // Labor Day
      fixed(5, 8),     // Victory in Europe Day
      fromEaster(39),  // Ascension Day
      fromEaster(50),  // Pentecost Monday
      fixed(7, 14),    // Bastille Day
      fixed(8, 15),    // Assumption of Mary
      fixed(11, 1),    // All Saints' Day
      fixed(11, 11),   // Armistice Day
      fixed(12, 25),   // Christmas Day
    ];
  }

  /**
   * Generate French public holidays for a sliding window of years.
   * @param fromYear First year (inclusive)
   * @param toYear Last year (inclusive)
   * @returns Flat array of ISO date strings (YYYY-MM-DD)
   */
  static getFrenchHolidays(fromYear: number, toYear: number): string[] {
    const holidays: string[] = [];
    for (let y = fromYear; y <= toYear; y++) {
      holidays.push(...DateHelper.getFrenchHolidaysForYear(y));
    }
    return holidays;
  }

  static UTCNow(): Date {

    const date = new Date();
    const nowUtc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
      date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());

    return new Date(nowUtc);
  }

  /**
   * Paris wall clock of an instant, for date/time pickers
   * @param value UTC instant
   * @returns Moment whose YYYY-MM-DD and HH:mm match Europe/Paris
   */
  static parisMoment(value?: Date | null): Moment {
    const source = value ?? new Date();
    const paris = source.toLocaleString('sv-SE', { timeZone: 'Europe/Paris' }).replace(' ', 'T');
    return moment(paris);
  }

  /**
   * Format an instant as a Paris date and time
   * @param value UTC instant
   * @returns Localized Paris timestamp
   */
  static formatParisDateTime(value: Date | number): string {
    const date = typeof value === 'number' ? new Date(value) : value;
    return date.toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Create an array with all hours of a day
   * @returns all hours of a day
   */
  static hoursOfDay = ((): { value: number; label: string }[] => {
    const steps: {value: number, label: string}[] = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toLocaleString('fr-FR', { minimumIntegerDigits: 2 });
      steps.push({ value: Number(hour+'00'), label: hour+':00' })
      steps.push({ value: Number(hour+'30'), label: hour+':30' })
    }
    return steps;
  })();
}
